/**
 * P2P relay from mobile → desktop using PeerJS cloud broker (no site backend).
 */

import Peer from 'peerjs';

const CHUNK_SIZE = 8000;

export function peerIdForSession(sessionSlug, peerPrefix = 'snid') {
    const safe = String(sessionSlug || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const prefix = String(peerPrefix || 'snid').replace(/[^a-zA-Z0-9_-]/g, '') || 'snid';
    return `${prefix}-${safe}`.slice(0, 63);
}

function waitPeerOpen(peer) {
    return new Promise((resolve, reject) => {
        peer.once('open', (id) => resolve(id));
        peer.once('error', reject);
    });
}

function waitConnOpen(conn) {
    return new Promise((resolve, reject) => {
        conn.once('open', resolve);
        conn.once('error', reject);
    });
}

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
            const s = r.result;
            const i = s.indexOf(',');
            resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

/**
 * Desktop: listen for incoming file batches from mobile.
 * @param {string} sessionSlug
 * @param {(files: File[]) => void} onReceiveFiles
 * @param {string} [peerPrefix]
 * @returns {() => void} cleanup
 */
export function createDesktopRelay(sessionSlug, onReceiveFiles, peerPrefix = 'snid') {
    const id = peerIdForSession(sessionSlug, peerPrefix);
    const peer = new Peer(id);

    const onConnection = (conn) => {
        let phase = 'idle';
        /** @type {{ name: string, mime: string, chunks: string[] } | null} */
        let cur = null;
        /** @type {File[]} */
        const batchFiles = [];

        const flushFile = () => {
            if (!cur || cur.chunks.length === 0) return;
            try {
                const b64 = cur.chunks.join('');
                const bin = atob(b64);
                const bytes = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                batchFiles.push(new File([bytes], cur.name, { type: cur.mime || 'image/jpeg' }));
            } catch {
                /* ignore corrupt chunk */
            }
            cur = null;
        };

        conn.on('data', (msg) => {
            if (!msg || typeof msg !== 'object') return;
            const { t } = msg;
            if (t === 'batch') {
                phase = 'batch';
                batchFiles.length = 0;
                cur = null;
                return;
            }
            if (phase !== 'batch') return;

            if (t === 'fn') {
                flushFile();
                cur = { name: msg.name || 'nid.jpg', mime: msg.mime || 'image/jpeg', chunks: [] };
                return;
            }
            if (t === 'fc' && cur && typeof msg.d === 'string') {
                cur.chunks.push(msg.d);
                return;
            }
            if (t === 'fd') {
                flushFile();
                return;
            }
            if (t === 'bd') {
                flushFile();
                phase = 'idle';
                if (batchFiles.length) {
                    onReceiveFiles(batchFiles.slice());
                    batchFiles.length = 0;
                }
                try {
                    conn.close();
                } catch {
                    /* ignore */
                }
            }
        });
    };

    peer.on('connection', onConnection);

    return () => {
        try {
            peer.destroy();
        } catch {
            /* ignore */
        }
    };
}

/**
 * Mobile: send image files to desktop peer for this session.
 */
export async function sendImagesToDesktop(sessionSlug, files, peerPrefix = 'snid') {
    if (!sessionSlug || !files?.length) return;

    const targetId = peerIdForSession(sessionSlug, peerPrefix);
    let lastErr;

    for (let attempt = 0; attempt < 5; attempt++) {
        let peer;
        try {
            peer = new Peer();
            await Promise.race([
                waitPeerOpen(peer),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 20000)),
            ]);

            const conn = peer.connect(targetId);
            await Promise.race([
                waitConnOpen(conn),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 20000)),
            ]);

            conn.send({ t: 'batch', count: files.length });

            for (const file of files) {
                const b64 = await fileToBase64(file);
                conn.send({ t: 'fn', name: file.name, mime: file.type || 'image/jpeg' });
                for (let i = 0; i < b64.length; i += CHUNK_SIZE) {
                    conn.send({ t: 'fc', d: b64.slice(i, i + CHUNK_SIZE) });
                    await new Promise((r) => setTimeout(r, 4));
                }
                conn.send({ t: 'fd' });
            }
            conn.send({ t: 'bd' });

            await new Promise((r) => setTimeout(r, 400));
            try {
                conn.close();
            } catch {
                /* ignore */
            }
            try {
                peer.destroy();
            } catch {
                /* ignore */
            }
            return;
        } catch (e) {
            lastErr = e;
            try {
                peer?.destroy();
            } catch {
                /* ignore */
            }
            await new Promise((r) => setTimeout(r, 600 + attempt * 400));
        }
    }

    throw new Error(
        lastErr?.message
        || 'Could not connect to desktop. Keep the signature pad page open on your computer.',
    );
}
